import React from 'react';
import {Form, Formik} from 'formik';
import {useDispatch} from 'react-redux';
import {saveWorkExperience} from 'shared/portfolio/actions/work-experiences';
import {validateLength} from 'shared/utils';

interface CommonFields {
  _id: string;
  role: string;
  place: string;
  startYear: number;
  startMonth: number;
  startDay: number;
  hoursPerWeek: number;
  responsibilities: string;
  paid: boolean;
  familyService: boolean;

  // not from the API, FE only
  editing?: boolean;
  new?: boolean;
  scrollTo?: boolean;
}

interface CurrentWorkplace extends CommonFields {
  endYear: null;
  endMonth: null;
  endDay: null;
  currentWorkplace: true;
}

interface PastWorkplace extends CommonFields {
  endYear: number;
  endMonth: number;
  endDay: number;
  currentWorkplace: false;
}

export type WorkExperience = CurrentWorkplace | PastWorkplace;

export type WorkExperienceForm = Omit<
  WorkExperience,
  'startYear' | 'startMonth' | 'startDay' | 'hoursPerWeek'
> & {
  startYear: number | null;
  startMonth: number | null;
  startDay: number | null;
  hoursPerWeek: number | '';
};

interface Props {
  initialValues: WorkExperienceForm;
  children: React.ReactNode;
  onSuccess?: () => void;
}

const WorkExperienceFormWrapper = ({initialValues, children, onSuccess}: Props) => {
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: Partial<Record<keyof WorkExperienceForm, string>> = {};
        const today = new Date();
        const roleValidation = validateLength('Title', values.role, 1000);
        const placeValidation = validateLength('Place name', values.place, 1000);
        const responsibilitiesValidation = validateLength(
          'Responsibilities',
          values.responsibilities,
          1000
        );

        if (!values.role) {
          errors.role = 'Title is required';
        } else if (roleValidation) {
          errors.role = roleValidation;
        }

        if (!values.place) {
          errors.place = 'Work location is required';
        } else if (placeValidation) {
          errors.place = placeValidation;
        }

        if (values.hoursPerWeek === '') {
          errors.hoursPerWeek = 'Hours per week worked is required';
        }

        if (!values.responsibilities) {
          errors.responsibilities = 'Responsibilities is required';
        } else if (responsibilitiesValidation) {
          errors.responsibilities = responsibilitiesValidation;
        }

        if (values.startMonth === null) {
          errors.startMonth = 'Start month is required';
        }

        if (values.startDay === null) {
          errors.startDay = 'Start day is required';
        }

        if (!values.startYear) {
          errors.startYear = 'Start year is required';
        }

        if (values.startMonth !== null && values.startDay !== null && values.startYear) {
          const startDate = new Date(values.startYear, values.startMonth, values.startDay);

          if (startDate.getFullYear() > today.getFullYear()) {
            errors.startYear = 'Date must not be in the future';
          } else if (startDate > today) {
            errors.startMonth = 'Date must not be in the future';
          }
        }

        if (!values.currentWorkplace) {
          if (values.endMonth === null) {
            errors.endMonth = 'End month is required';
          }

          if (values.endDay === null) {
            errors.endDay = 'End day is required';
          }

          if (!values.endYear) {
            errors.endYear = 'End year is required';
          }

          if (
            values.startMonth !== null &&
            values.startDay !== null &&
            values.startYear &&
            values.endMonth !== null &&
            values.endDay !== null &&
            values.endYear
          ) {
            const startDate = new Date(values.startYear, values.startMonth, values.startDay);
            const endDate = new Date(values.endYear, values.endMonth, values.endDay);

            if (endDate.getFullYear() > today.getFullYear()) {
              errors.endYear = 'Date must not be in the future';
            } else if (endDate > today) {
              errors.endMonth = 'Date must not be in the future';
            } else if (startDate.getFullYear() > endDate.getFullYear()) {
              errors.endYear = 'Start date must not be after end date';
            } else if (startDate > endDate) {
              errors.endMonth = 'Start date must not be after end date';
            }
          }
        }

        return errors;
      }}
      onSubmit={async (values, formik) => {
        try {
          await dispatch(saveWorkExperience(values));
          onSuccess?.();
        } catch (errors: $TSFixMe) {
          Object.keys(errors).forEach((field) => {
            if (errors[field]) {
              formik.setFieldError(field, errors[field]);
            }
          });
        }
      }}
    >
      <Form autoComplete="off">{children}</Form>
    </Formik>
  );
};

export default WorkExperienceFormWrapper;
